import { Grid, Menu, MenuItem } from '@material-ui/core';
import { AxiosError } from 'axios';
import LayoutContext from 'components/Layout/LayoutContext';
import { sortAnexos } from 'components/Modal/CadastroPautaModal/CadastroPautaModal';
import AnexoPautaItem from 'components/Modal/CadastroPautaModal/components/AnexoPautaItem/AnexoPautaItem';
import PreAnexoPautaItem from 'components/Modal/CadastroPautaModal/components/PreAnexoPautaItem/PreAnexoPautaItem';
import GenericModal, {
  IGenericModalProps,
} from 'components/Modal/__LEGACY__/GenericModal/GenericModal';
import { AnexoEnum, AnexoPautaType } from 'helpers/enums/anexoPauta';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { languageValues } from 'helpers/utils/languageUtils';
import translation from 'locales/translation';
import {
  AddCircleIcon,
  Button,
  ButtonFileUpload,
  ButtonThemes,
  CheckCircleIcon,
  Col,
  Collapse,
  FileTypes,
  Row,
  ThreeDotsLoader,
  TimesCircleIcon,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  deleteAnexoPauta,
  getAnexosPauta,
  postAnexoPauta,
  putAnexoPauta,
} from 'services/anexoPauta';
import { modalsActionsClose } from 'store/modules/ModalsActions';
import { IModalProps } from 'store/modules/ModalsReducer';
import { useStyles } from './styles';

interface IAnexoPautaModal extends IGenericModalProps {
  key: string;
  idPauta: number;
  idEvento: number;
}

interface Props extends IModalProps {
  genericProps: IAnexoPautaModal;
}

export default function AnexoPautaModal(props: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [preAnexo, setPreAnexo] = useState<AnexoPautaType>();

  const [anexos, setAnexos] = useState<AnexoPautaType[]>([]);

  const anexosEditados = useRef<number[]>([]);

  const [loading, setLoading] = useState(false);
  const [anexosLoading, setAnexosLoading] = useState(false);
  const [anexosDeletados, setAnexosDeletados] = useState<number[]>([]);

  const { showToast } = useContext(LayoutContext);

  const handleDeleteAnexo = useCallback(
    (id: number | undefined, idTemporarioFront?: number) => {
      if (id) {
        if (anexosEditados.current.includes(id)) {
          anexosEditados.current = anexosEditados.current.filter(
            (a) => a !== id
          );
        }
        setAnexosDeletados(anexosDeletados.concat(id));
      } else if (idTemporarioFront) {
        setAnexos(
          anexos.filter((a) => a.idTemporarioFront !== idTemporarioFront)
        );
      }
    },
    [anexos, anexosDeletados]
  );

  const [cancelConfirm, setCancelConfirm] = useState(false);
  const cancelAnchor = useRef<HTMLElement>();

  const toClose = useCallback(
    (event?: MouseEvent<HTMLButtonElement>): void => {
      if (
        event &&
        (anexos.filter((anexo) => !anexo.oidAnexoPauta).length ||
          anexosDeletados.length ||
          anexosEditados.current.length)
      ) {
        cancelAnchor.current = event.target as HTMLElement;
        setCancelConfirm(!cancelConfirm);
      } else if (props.genericProps.key) {
        dispatch(modalsActionsClose(props.genericProps?.key));
      }
    },
    [
      anexos,
      anexosDeletados.length,
      cancelConfirm,
      dispatch,
      props.genericProps?.key,
    ]
  );

  const carregarAnexos = useCallback(async () => {
    if (!props.genericProps.idPauta) return [];
    setAnexosLoading(true);
    try {
      const req = await getAnexosPauta(
        props.genericProps.idEvento,
        props.genericProps.idPauta
      );
      setAnexos(req.data);
    } catch (err) {
      showToast({
        pauseOnFocusLoss: true,
        theme: ToastTypes.Error,
        showStatusBar: true,
        timeout: 4000,
        label: languageValues.toasts.error.loadingAnexos,
      });
      toClose();
    } finally {
      setAnexosLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.genericProps.idEvento, props.genericProps.idPauta, toClose]);

  useEffect(() => {
    carregarAnexos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toSubmit = useCallback(async () => {
    setLoading(true);
    try {
      const novosAnexos = anexos.filter((a) => !a.oidAnexoPauta);
      const all = await Promise.all([
        ...novosAnexos.map((a) => {
          return postAnexoPauta(
            props.genericProps.idEvento,
            props.genericProps.idPauta,
            a
          );
        }),
        ...anexosEditados.current.map((ed) => {
          const a = anexos.find((an) => an.oidAnexoPauta === ed);
          if (a) {
            return putAnexoPauta(
              props.genericProps.idEvento,
              props.genericProps.idPauta,
              a
            );
          }
          return Promise.resolve();
        }),
        ...anexosDeletados.map((del) => {
          return deleteAnexoPauta(
            props.genericProps.idEvento,
            props.genericProps.idPauta,
            del
          );
        }),
      ]);
      toClose();
      if (all.length) {
        toast.success(translation.CadastroPautaModal.successSavingAnexos);
      }
    } catch (error) {
      showToast({
        pauseOnFocusLoss: true,
        theme: ToastTypes.Error,
        showStatusBar: true,
        timeout: 4000,
        label: languageValues.toasts.error.keyErrors(
          (error as AxiosError<any>).response?.data.message,
          translation.CadastroPautaModal.errorSavingAnexos
        ),
      });
    } finally {
      setLoading(false);
    }
  }, [
    anexos,
    anexosDeletados,
    props.genericProps.idEvento,
    props.genericProps.idPauta,
    showToast,
    toClose,
  ]);

  return (
    <GenericModal
      open={props.open ?? false}
      title={languageValues.modals.anexoPauta.title}
      content={
        <Grid>
          <Row>
            <Col noPadding gap={16} className={classes.actions}>
              <ButtonFileUpload
                allowedFileTypes={[
                  FileTypes.PDF,
                  FileTypes.JPG,
                  FileTypes.JPEG,
                  FileTypes.PNG,
                ]}
                leftIcon={<AddCircleIcon />}
                label={translation.CadastroPautaModal.labelAdicioneDocumento}
                onTypeNotAllowed={() => {
                  showToast({
                    pauseOnFocusLoss: true,
                    theme: ToastTypes.Error,
                    showStatusBar: true,
                    timeout: 4000,
                    label: languageValues.toasts.error.typeNotAllowed,
                  });
                }}
                onChange={(e: File | File[]) => {
                  if ((e as File).size > 35000000) {
                    showToast({
                      pauseOnFocusLoss: true,
                      theme: ToastTypes.Error,
                      showStatusBar: true,
                      timeout: 4000,
                      label: languageValues.toasts.error.uploadSize,
                    });
                  } else {
                    setPreAnexo({
                      tipoAnexo: AnexoEnum.DOCUMENTO,
                      descricao: '',
                      flgDisponivelAssociado: true,
                      file: e as File,
                    });
                  }
                }}
              />
              <Button
                leftIcon={<AddCircleIcon />}
                onClick={() => {
                  setPreAnexo({
                    tipoAnexo: AnexoEnum.VIDEO,
                    descricao: '',
                    url: '',
                    flgDisponivelAssociado: true,
                  });
                }}
              >
                {translation.CadastroPautaModal.labelAdicioneVideo}
              </Button>
            </Col>
          </Row>
          <Row style={{ marginBottom: preAnexo ? '16px' : 0 }}>
            <PreAnexoPautaItem
              anexo={preAnexo}
              onCancel={() => {
                setPreAnexo(undefined);
              }}
              onAdd={(newAnexo) => {
                setAnexos(anexos.concat(newAnexo));
                setPreAnexo(undefined);
              }}
            />
          </Row>
          <Row>
            <Collapse animateOpacity opened={anexosLoading}>
              <Col centralized>
                <ThreeDotsLoader size={2} />
              </Col>
            </Collapse>
          </Row>
          <Collapse animateOpacity opened={anexos.length > 0}>
            <Row className={classes.itensAnexoPautaRow}>
              {!anexos.length
                ? undefined
                : anexos.sort(sortAnexos).map((anexo, i) => {
                    return !anexo.oidAnexoPauta ||
                      !anexosDeletados.includes(anexo.oidAnexoPauta) ? (
                      <AnexoPautaItem
                        idEvento={props.genericProps.idEvento}
                        idPauta={props.genericProps.idPauta}
                        key={
                          anexo.oidAnexoPauta ??
                          anexo.idTemporarioFront ??
                          getUniqueKey()
                        }
                        anexo={anexo}
                        onRemove={(id) =>
                          handleDeleteAnexo(id, anexo.idTemporarioFront)
                        }
                        onEdit={(anexo) => {
                          if (
                            anexo.oidAnexoPauta &&
                            !anexosEditados.current.includes(
                              anexo.oidAnexoPauta
                            )
                          ) {
                            anexosEditados.current.push(anexo.oidAnexoPauta);
                          }
                          setAnexos(
                            anexos.map((a, index) => {
                              if (
                                (a.oidAnexoPauta &&
                                  a.oidAnexoPauta === anexo.oidAnexoPauta) ||
                                index === i
                              ) {
                                return anexo;
                              }
                              return a;
                            })
                          );
                        }}
                      />
                    ) : undefined;
                  })}
            </Row>
          </Collapse>
          <Collapse
            animateOpacity
            opened={
              !anexosLoading &&
              (!anexos.length ||
                !anexos.filter(
                  (a) => !anexosDeletados.includes(a.oidAnexoPauta!)
                ).length)
            }
          >
            <div>{languageValues.modals.anexoPauta.semAnexos}</div>
          </Collapse>
        </Grid>
      }
      genericProps={{
        id: 'anexo-pauta-modal',
        maxWidth: 'sm',
        onClose: toClose,
        footerContent: (
          <Col noPadding end gap={16}>
            <Menu
              open={cancelConfirm}
              onClose={() => setCancelConfirm(false)}
              anchorEl={cancelAnchor.current}
            >
              <MenuItem disabled>
                {languageValues.modals.anexoPauta.cancelSure}
                <br />
                {languageValues.modals.anexoPauta.cancelSubtitle}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  toClose();
                  setCancelConfirm(false);
                }}
              >
                {languageValues.buttons.yes}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setCancelConfirm(false);
                }}
              >
                {languageValues.buttons.no}
              </MenuItem>
            </Menu>
            <Button
              style={{ marginLeft: '0', marginRight: '0' }}
              leftIcon={<TimesCircleIcon />}
              theme={ButtonThemes.Secondary}
              onClick={toClose}
            >
              {translation.CadastroPautaModal.cancelButton}
            </Button>
            <Button
              style={{ marginLeft: '0', marginRight: '0' }}
              leftIcon={<CheckCircleIcon />}
              theme={ButtonThemes.Primary}
              onClick={toSubmit}
              disabled={anexosLoading || loading}
              loading={anexosLoading || loading}
            >
              {languageValues.buttons.save}
            </Button>
          </Col>
        ),
      }}
    />
  );
}
